import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/trees`;

export const treeApi = axios.create({ baseURL: API, withCredentials: true });

const TOKEN_KEY = "tms_token";

// Sidebar/route priority order — used to pick a sensible landing page for a custom
// role after login (the first one they actually have access to).
const PAGE_ROUTES = [["dashboard", "/trees"], ["field", "/trees/field"], ["stock", "/trees/pallets"], ["collections", "/trees/loads"], ["yard", "/trees/yard"]];

treeApi.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

const TreeAuthContext = createContext({
  user: null, loading: true, login: async () => {}, logout: async () => {},
  settings: { trees_per_pallet: 100, pallets_per_trailer: 10 }, refreshSettings: async () => {},
  permissions: {}, roleName: "", refreshPermissions: async () => {},
});

export function TreeAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ trees_per_pallet: 100, pallets_per_trailer: 10 });
  const [permissions, setPermissions] = useState({});
  const [roleName, setRoleName] = useState("");

  const refreshPermissions = useCallback(async () => {
    try {
      const r = await treeApi.get("/roles/mine");
      setPermissions(r.data.permissions || {});
      setRoleName(r.data.role_name || "");
      return r.data.permissions || {};
    } catch {
      setPermissions({});
      setRoleName("");
      return {};
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const r = await treeApi.get("/auth/me");
      setUser(r.data);
      await refreshPermissions();
    } catch {
      setUser(false);
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, [refreshPermissions]);

  const refreshSettings = useCallback(async () => {
    try {
      const r = await treeApi.get("/settings");
      setSettings(r.data);
    } catch { /* keep defaults */ }
  }, []);

  useEffect(() => { refresh(); refreshSettings(); }, [refresh, refreshSettings]);

  const applySessionData = useCallback(async (data) => {
    if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    const perms = await refreshPermissions();
    let redirectTo = "/trees";
    if (data.role !== "admin" && data.role !== "field_status" && data.role !== "field_collector") {
      const firstPage = PAGE_ROUTES.find(([page]) => perms?.[page]?.access);
      redirectTo = firstPage ? firstPage[1] : "/trees"; // falls through to the friendly no-access message if nothing is ticked yet
    }
    return { ...data, redirectTo };
  }, [refreshPermissions]);

  const login = useCallback(async (email, password) => {
    const r = await treeApi.post("/auth/login", { email, password });
    return applySessionData(r.data);
  }, [applySessionData]);

  const loginWithPasskey = useCallback(async () => {
    const { startAuthentication } = await import("@simplewebauthn/browser");
    const optsRes = await treeApi.post("/auth/passkeys/authentication/options");
    const options = JSON.parse(optsRes.data);
    const assertion = await startAuthentication({ optionsJSON: options });
    const r = await treeApi.post("/auth/passkeys/authentication/verify", assertion);
    return applySessionData(r.data);
  }, [applySessionData]);

  const logout = useCallback(async () => {
    try { await treeApi.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
    setPermissions({});
    setRoleName("");
  }, []);

  return (
    <TreeAuthContext.Provider value={{ user, loading, login, loginWithPasskey, logout, refresh, settings, refreshSettings, permissions, roleName, refreshPermissions }}>
      {children}
    </TreeAuthContext.Provider>
  );
}

export function useTreeAuth() { return useContext(TreeAuthContext); }
